import React, { useEffect, useState } from 'react';
import { Modal, Button, Checkbox, Spin, Alert } from 'antd';
import ReactMarkdown from 'react-markdown';
import PropTypes from 'prop-types';

import { getTnCDocument } from 'utils/apis/tnc';

import './TermsAndConditionsModal.css';

const TermsAndConditionsModal = ({ visible, onAgree, acceptError, isAccepting, onClearAcceptError, title }) => {
  const [markdown, setMarkdown] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    if (!visible) {
      setAgreed(false);
      if (typeof onClearAcceptError === 'function') {
        onClearAcceptError();
      }
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setLoadError(false);

    const loadDocument = async () => {
      try {
        const response = await getTnCDocument();
        const doc = response.data;

        if (cancelled) {
          return;
        }

        if (doc && doc.content) {
          setMarkdown(doc.content);
        } else {
          throw new Error('Empty document');
        }
      } catch {
        if (!cancelled) {
          setLoadError(true);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadDocument();

    return () => {
      cancelled = true;
    };
  }, [visible, onClearAcceptError]);

  const handleAgree = async () => {
    if (!agreed || loading || isAccepting) {
      return;
    }
    if (typeof onAgree === 'function') {
      await onAgree();
    }
  };

  return (
    <Modal
      title={
        <span
          style={{
            fontWeight: 'bold'
          }}
        >
          {title}
        </span>
      }
      visible={visible}
      width={720}
      destroyOnClose
      closable={false}
      maskClosable={false}
      keyboard={false}
      bodyStyle={{ paddingTop: 12 }}
      footer={
        <div style={{ textAlign: 'right' }}>
          <Button type="primary" disabled={!agreed || loading || isAccepting} loading={isAccepting} onClick={handleAgree}>
            Accept
          </Button>
        </div>
      }
    >
      <Spin spinning={loading || isAccepting}>
        {acceptError ? (
          <Alert type="error" showIcon message="Could not save acceptance" description={acceptError} style={{ marginBottom: 12 }} />
        ) : null}
        {loadError ? (
          <Alert
            type="error"
            showIcon
            message="Could not load Terms & Conditions"
            description="Please check your connection and refresh the page to try again."
          />
        ) : (
          <div className="terms-modal-markdown">
            <ReactMarkdown source={markdown} />
          </div>
        )}
        {!loading && (
          <div className="terms-modal-agree-row">
            <Checkbox checked={agreed} onChange={e => setAgreed(e.target.checked)} disabled={isAccepting}>
              {loadError
                ? 'I have reviewed the Terms & Conditions and agree to proceed once the document is available.'
                : 'I have read, understood, and agree to these Terms & Conditions, including how personal data is processed as described above.'}
            </Checkbox>
          </div>
        )}
      </Spin>
    </Modal>
  );
};

TermsAndConditionsModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onAgree: PropTypes.func,
  acceptError: PropTypes.string,
  isAccepting: PropTypes.bool,
  onClearAcceptError: PropTypes.func,
  title: PropTypes.string
};

TermsAndConditionsModal.defaultProps = {
  onAgree: undefined,
  acceptError: null,
  isAccepting: false,
  onClearAcceptError: undefined,
  title: 'Personal Data Processing Terms & Conditions'
};

export default TermsAndConditionsModal;
